import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Coins, Fire, Lightbulb, Users, Handshake, TrendDown, TrendUp,
  ChartLine, Scales, Package, MegaphoneSimple, ShieldCheck, Cube,
  ArrowsDownUp, Book, CaretDown, CaretRight, CheckCircle, Link, Drop,
  ShoppingCartSimple, Storefront, FileCode, HashStraight, Lightning
} from "@phosphor-icons/react";
import WhyCryptoDefi from "@/pages/WhyCryptoDefi";
import BlockchainEducation from "@/pages/BlockchainEducation";
import WhatIsCrypto from "@/pages/WhatIsCrypto";
import { Question as QuestionIcon } from "@phosphor-icons/react";

const TOPICS = [
  {
    id: "how-to-buy",
    icon: ShoppingCartSimple,
    color: "#10b981",
    title: "How to Buy",
    subtitle: "Step-by-step guide to purchasing crypto and stocks",
    content: [
      {
        heading: "Step 1 — Choose Your Platform",
        text: "Pick a trusted exchange or broker. For crypto, popular options are Coinbase, Kraken, or Binance. For stocks, use Fidelity, Charles Schwab, or Robinhood. For on-chain DeFi trading, you'll need a self-custody wallet like MetaMask, Phantom, or Rabby.",
      },
      {
        heading: "Step 2 — Create & Verify Your Account",
        text: "Sign up with your email, set a strong password, and enable 2-Factor Authentication (2FA). Most platforms require identity verification (KYC) — have your ID and a selfie ready. This usually takes minutes to a few hours.",
      },
      {
        heading: "Step 3 — Deposit Funds",
        text: "Add money via bank transfer (ACH), debit card, or wire. Bank transfers are cheapest but take 1–3 days. Debit cards are instant but charge a small fee. For DeFi, you'll send crypto from an exchange to your wallet address.",
      },
      {
        heading: "Step 4 — Find the Asset You Want",
        text: "Use the search bar to find the token or stock by name or ticker symbol (e.g., BTC for Bitcoin, ETH for Ethereum, NVDA for Nvidia). Check the current price, 24-hour change, and volume before buying.",
      },
      {
        heading: "Step 5 — Place Your Order",
        items: [
          "Market Order — Buy immediately at the current price. Simplest option, best for beginners.",
          "Limit Order — Set the price you want to pay. The order fills only when the price reaches your target. Saves money but may not fill.",
          "Dollar-Cost Average (DCA) — Buy a fixed dollar amount on a regular schedule (e.g., $50 every week). Reduces the impact of volatility over time.",
        ],
      },
      {
        heading: "Step 6 — Secure Your Purchase",
        items: [
          "For small amounts: Leaving funds on a reputable exchange is fine.",
          "For large amounts: Transfer to a hardware wallet (Ledger, Trezor) for maximum security.",
          "Never share your seed phrase or private keys with anyone.",
          "Write your recovery phrase on paper and store it safely offline — not digitally.",
        ],
      },
      {
        heading: "Common Mistakes to Avoid",
        items: [
          "Buying based on hype without doing your own research.",
          "Going all-in at once instead of spreading purchases over time.",
          "Ignoring fees — check trading fees, withdrawal fees, and gas fees before buying.",
          "Not setting a plan — know your entry price and how long you intend to hold.",
        ],
      },
    ],
  },
  {
    id: "how-to-sell",
    icon: Storefront,
    color: "#ef4444",
    title: "How to Sell",
    subtitle: "Step-by-step guide to selling and taking profits",
    content: [
      {
        heading: "Step 1 — Decide Why You're Selling",
        items: [
          "Taking profits — You've hit your price target or portfolio goal.",
          "Cutting losses — The asset dropped below your stop-loss level.",
          "Rebalancing — You want to shift funds into a different asset.",
          "Need cash — You need the money for real-life expenses.",
        ],
      },
      {
        heading: "Step 2 — Choose How Much to Sell",
        text: "You don't have to sell everything. Consider selling in portions: sell 25% at your first target, another 25% at the next, and hold the rest. This locks in gains while keeping upside exposure. Professional traders rarely sell 100% at once.",
      },
      {
        heading: "Step 3 — Pick Your Order Type",
        items: [
          "Market Order — Sell immediately at the current price. Fast but you might get a slightly worse price in volatile markets.",
          "Limit Order — Set the minimum price you're willing to accept. Fills only at your price or better. Good for setting profit targets in advance.",
          "Stop-Loss Order — Automatically sells if the price drops to a set level. Protects you from big losses while you're away.",
          "Trailing Stop — Follows the price up by a percentage, then sells if it reverses. Locks in profits during a rally.",
        ],
      },
      {
        heading: "Step 4 — Execute the Sale",
        text: "Go to your exchange or broker, find the asset, and select 'Sell.' Enter your amount and order type. Double-check the details (amount, price, fees) before confirming. For DeFi, connect your wallet to the DEX, select the token, and approve the swap.",
      },
      {
        heading: "Step 5 — Withdraw Your Funds",
        items: [
          "After selling, your funds sit as USD (or stablecoin) on the exchange.",
          "To cash out: Go to 'Withdraw' and send to your bank account. ACH takes 1–3 business days.",
          "Alternative: Convert to a stablecoin (USDC, USDT) if you want to stay in crypto but avoid volatility.",
          "Keep some funds on the exchange if you plan to re-enter the market.",
        ],
      },
      {
        heading: "Step 6 — Record for Taxes",
        text: "Every sale is a taxable event in most countries. Record the date, amount sold, sale price, and original purchase price. Short-term gains (held under 1 year) are taxed as income. Long-term gains (held over 1 year) get a lower tax rate. Use tools like Koinly, CoinTracker, or TurboTax to track automatically.",
      },
      {
        heading: "Common Mistakes to Avoid",
        items: [
          "Panic selling during a dip — Have a plan and stick to it.",
          "Selling too early out of fear — Set price targets based on research, not emotion.",
          "Selling too late out of greed — 'Just a little more' often turns gains into losses.",
          "Ignoring fees and slippage — Check the total cost before confirming.",
          "Forgetting taxes — Track every trade to avoid surprises at tax time.",
        ],
      },
    ],
  },
  {
    id: "smart-contracts",
    icon: FileCode,
    color: "#8b5cf6",
    title: "What is a Smart Contract?",
    subtitle: "Self-executing code that runs on the blockchain",
    content: [
      {
        heading: "The Simple Explanation",
        text: "A smart contract is a program that lives on a blockchain. It automatically does what it's told to do when certain conditions are met — no middleman needed. Think of it like a vending machine: you put in the right amount of money, press the button, and the machine gives you your item. No cashier required. A smart contract works the same way, but with digital assets and rules written in code.",
      },
      {
        heading: "How Does It Work?",
        items: [
          "Someone writes the rules in code (e.g., 'If Person A sends 1 ETH, send them 1000 tokens back').",
          "The code gets deployed to the blockchain — once it's there, nobody can change it.",
          "Anyone can interact with it by sending a transaction to the contract's address.",
          "The blockchain executes the code automatically and records the result forever.",
        ],
      },
      {
        heading: "Real-World Examples",
        items: [
          "Token swaps — You send Token A to a smart contract, it sends you Token B at the current price. That's how DEXes like Uniswap work.",
          "Staking — You lock your tokens in a smart contract, and it automatically calculates and distributes your rewards over time.",
          "NFTs — A smart contract defines who owns a digital item and handles transfers when someone buys or sells it.",
          "Lending — You deposit crypto as collateral into a smart contract, and it lets you borrow other assets against it. If your collateral drops too low, the contract automatically liquidates it.",
        ],
      },
      {
        heading: "Why It Matters",
        text: "Smart contracts remove the need to trust a company or person to hold your money or keep their promise. The code is the guarantee. Every interaction is transparent and recorded on the blockchain for anyone to verify. This is why DeFi (Decentralized Finance) exists — entire financial systems running on smart contracts with no banks involved.",
      },
      {
        heading: "Things to Be Careful About",
        items: [
          "Bugs in the code — If a smart contract has a flaw, hackers can exploit it. Millions have been lost this way.",
          "Once deployed, it can't be changed — This is a feature (trust), but also a risk if there's a mistake.",
          "Always check if a contract has been audited by a reputable security firm before putting money into it.",
          "Just because something is a 'smart contract' doesn't mean it's smart — it only does exactly what the code says, nothing more.",
        ],
      },
    ],
  },
  {
    id: "contract-address",
    icon: HashStraight,
    color: "#06b6d4",
    title: "What is a Contract Address?",
    subtitle: "The unique ID that points to a smart contract on the blockchain",
    content: [
      {
        heading: "The Simple Explanation",
        text: "A contract address is like a street address, but for a program on the blockchain. Just like every house has a unique address so mail can find it, every smart contract has a unique address so people and wallets can interact with it. It looks like a long string of letters and numbers, for example: 0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39 (that's the HEX token contract on Ethereum).",
      },
      {
        heading: "Why Does It Matter?",
        items: [
          "It's how you verify you're interacting with the real token and not a fake copy. Scammers create tokens with the same name and symbol — the contract address is the only way to tell them apart.",
          "When you add a custom token to your wallet (like MetaMask), you paste the contract address so your wallet knows which token to track.",
          "When you trade on a DEX, the contract address is what connects you to the correct trading pair.",
        ],
      },
      {
        heading: "How to Find a Contract Address",
        items: [
          "CoinGecko or CoinMarketCap — Look up the token and find the 'Contract' section. It shows the address for each chain the token is on.",
          "DexScreener — Search for the token and the contract address is shown in the pair details.",
          "Block explorers — Use Etherscan (Ethereum), BscScan (BNB Chain), or Solscan (Solana) to look up any contract.",
          "The project's official website — Legitimate projects always list their contract address.",
        ],
      },
      {
        heading: "How to Use It Safely",
        items: [
          "Always copy the contract address from a trusted source (CoinGecko, official website, or the project's verified social media).",
          "Never trust a contract address someone sends you in a DM or comment — scammers do this constantly.",
          "Double-check the first and last few characters when pasting — some malware swaps addresses in your clipboard.",
          "On Ethereum, the address starts with '0x' and is 42 characters long. On Solana, it's a base58 string (letters and numbers, no 0x).",
        ],
      },
      {
        heading: "One Token, Multiple Addresses",
        text: "A token can exist on multiple blockchains, and each version has a different contract address. For example, USDC on Ethereum has a different contract address than USDC on Solana or Polygon. They're the same asset in concept, but they live on different chains. Always make sure you're using the contract address for the correct chain, or your funds could be lost.",
      },
    ],
  },
  {
    id: "supply-demand",
    icon: ArrowsDownUp,
    color: "#3b82f6",
    title: "Supply & Demand Metrics",
    subtitle: "Understanding tokenomics fundamentals",
    content: [
      {
        heading: "What to Look For",
        text: "Supply and demand is the most fundamental driver of any asset's price. In crypto, this is encoded directly into the protocol through tokenomics.",
      },
      {
        heading: "Total Supply vs. Circulating Supply",
        text: "Total Supply is the maximum number of tokens that will ever exist. Circulating Supply is what's currently available on the market. A large gap means future dilution risk. For example, Bitcoin has a fixed total supply of 21 million coins, with ~19.6M currently circulating. Compare this to tokens where only 10-20% of supply is circulating \u2014 those have massive unlock risk ahead.",
      },
      {
        heading: "Market Cap vs. Fully Diluted Valuation (FDV)",
        text: "Market Cap = Circulating Supply \u00d7 Price. FDV = Total Supply \u00d7 Price. When FDV is dramatically higher than Market Cap, it signals heavy future dilution. A token with $100M market cap but $2B FDV means 95% of tokens haven't hit the market yet.",
      },
      {
        heading: "Key Metrics to Track",
        items: [
          "Daily trading volume vs. market cap (liquidity ratio)",
          "Token unlock schedules (vesting cliffs, linear unlocks)",
          "Exchange inflows/outflows (selling pressure indicators)",
          "Top holder concentration (whale risk)",
          "Burned/locked supply percentage",
        ],
      },
    ],
  },
  {
    id: "meme-narrative",
    icon: MegaphoneSimple,
    color: "#f59e0b",
    title: "Meme Narrative & Story",
    subtitle: "Community-driven value and viral momentum",
    content: [
      {
        heading: "Why Narrative Matters",
        text: "In crypto, narrative can drive price more than fundamentals in the short-to-medium term. Meme tokens like DOGE and PEPE prove that community belief and cultural momentum create real market value.",
      },
      {
        heading: "Evaluating a Meme/Narrative Token",
        items: [
          "Community size and engagement (Twitter/X followers, Telegram/Discord members, Reddit activity)",
          "Organic growth vs. bot-driven metrics",
          "Cultural relevance \u2014 does the meme tap into current internet culture?",
          "Celebrity or influencer endorsements (double-edged sword)",
          "Social volume trends \u2014 is attention growing or fading?",
        ],
      },
      {
        heading: "Red Flags",
        items: [
          "Anonymous team with no accountability",
          "Token distribution heavily concentrated in few wallets",
          "No liquidity lock or very short lock period",
          "Sudden, coordinated shilling across social media",
          "Copycat projects with no original thesis",
        ],
      },
      {
        heading: "The Lifecycle",
        text: "Most meme narratives follow a pattern: Discovery \u2192 Viral Spread \u2192 Peak Hype \u2192 Correction \u2192 Either death or evolution into utility. The best meme tokens (like DOGE, SHIB) survived by building utility after the meme phase.",
      },
    ],
  },
  {
    id: "utility",
    icon: Cube,
    color: "#8b5cf6",
    title: "Utility & Use Cases",
    subtitle: "Real-world value and protocol demand",
    content: [
      {
        heading: "What is Token Utility?",
        text: "Utility tokens have a specific function within their protocol. Ethereum's ETH pays for gas fees, Chainlink's LINK pays oracle node operators, Filecoin's FIL pays for decentralized storage. The more the protocol is used, the more demand for the token.",
      },
      {
        heading: "Types of Utility",
        items: [
          "Gas/Transaction fees \u2014 Required to use the network (ETH, SOL, AVAX)",
          "Governance \u2014 Voting power over protocol decisions (UNI, AAVE, COMP)",
          "Staking \u2014 Lock tokens to secure the network and earn yields (ETH, ADA, DOT)",
          "Access/Membership \u2014 Required to use platform features",
          "Payment \u2014 Medium of exchange within ecosystem",
          "Revenue sharing \u2014 Token holders earn protocol fees",
        ],
      },
      {
        heading: "Evaluating Utility Strength",
        items: [
          "Is the token REQUIRED to use the protocol, or optional?",
          "How many active users does the protocol have? (DAU/MAU metrics)",
          "Is there a fee-burning mechanism creating deflationary pressure?",
          "Total Value Locked (TVL) in DeFi protocols",
          "Developer activity (GitHub commits, new contracts deployed)",
          "Cross-chain integrations and partnerships",
        ],
      },
    ],
  },
  {
    id: "inflationary",
    icon: TrendUp,
    color: "#ef4444",
    title: "Inflationary Tokens",
    subtitle: "Expanding supply economics",
    content: [
      {
        heading: "What Makes a Token Inflationary?",
        text: "Inflationary tokens have a continuously increasing supply. New tokens are minted over time, typically as rewards for validators, stakers, or liquidity providers. This creates constant sell pressure unless demand grows faster than emission.",
      },
      {
        heading: "Examples",
        items: [
          "Ethereum (pre-Merge) \u2014 ~4.7% annual issuance to miners",
          "Dogecoin \u2014 5 billion new DOGE minted annually, no cap (currently ~3.4% inflation)",
          "Polkadot (DOT) \u2014 ~7-10% annual inflation to reward stakers",
          "Solana (SOL) \u2014 Started at 8% inflation, decreasing 15% annually to 1.5% floor",
        ],
      },
      {
        heading: "What to Watch",
        items: [
          "Annual emission/inflation rate \u2014 how fast is supply growing?",
          "Emission schedule \u2014 is inflation decreasing over time?",
          "Staking yield vs. inflation \u2014 does staking outpace dilution?",
          "Real yield = staking APY - inflation rate. If negative, stakers are losing value",
          "Protocol revenue \u2014 can fees offset emission?",
        ],
      },
      {
        heading: "Key Insight",
        text: "An inflationary token isn't automatically bad. If the protocol generates enough demand (fees, utility, adoption) to outpace new issuance, the token can still appreciate. Ethereum proved this post-Merge by becoming deflationary during high-demand periods.",
      },
    ],
  },
  {
    id: "deflationary",
    icon: TrendDown,
    color: "#10b981",
    title: "Deflationary Tokens",
    subtitle: "Shrinking supply economics",
    content: [
      {
        heading: "What Makes a Token Deflationary?",
        text: "Deflationary tokens have mechanisms that permanently remove tokens from circulation, reducing total supply over time. This creates scarcity \u2014 if demand stays constant, reduced supply pushes price up.",
      },
      {
        heading: "Burn Mechanisms",
        items: [
          "Transaction fee burns \u2014 A portion of every transaction fee is destroyed (Ethereum EIP-1559, BNB)",
          "Buyback and burn \u2014 Protocol uses revenue to buy and destroy tokens (BNB quarterly burn)",
          "Staking penalties \u2014 Slashing conditions burn staked tokens (Ethereum)",
          "Fixed-supply tokens \u2014 Bitcoin will never exceed 21 million coins",
          "Manual burns \u2014 Team periodically burns tokens from treasury",
        ],
      },
      {
        heading: "Case Studies",
        items: [
          "Bitcoin \u2014 21M fixed cap + lost coins = naturally deflationary. ~3.7M BTC estimated permanently lost",
          "Ethereum (post-Merge) \u2014 EIP-1559 burns base fees. During high network activity, more ETH burned than issued",
          "HEX \u2014 T-Share rate increases over time, stake penalties burn HEX, reducing effective supply",
          "BNB \u2014 Quarterly auto-burn based on price and block production until 100M supply reached",
        ],
      },
      {
        heading: "What to Verify",
        items: [
          "Check actual burn data on-chain (not just promises)",
          "Calculate net emission = new tokens minted - tokens burned",
          "Track circulating supply trend over time",
          "Verify burn address is a true burn (not a team-controlled wallet)",
        ],
      },
    ],
  },
  {
    id: "founder",
    icon: Users,
    color: "#06b6d4",
    title: "Founders & Team",
    subtitle: "Evaluating the people behind the project",
    content: [
      {
        heading: "Why Founders Matter",
        text: "In crypto, the team building the project is one of the strongest predictors of success. Look for founders with relevant experience, transparent communication, and aligned incentives with token holders.",
      },
      {
        heading: "What to Research",
        items: [
          "Team identity \u2014 Are founders doxxed (publicly identified) or anonymous?",
          "Track record \u2014 Have they built successful projects before?",
          "Technical credibility \u2014 Published research, patents, open-source contributions",
          "Token allocation \u2014 How much does the team hold? Is it vesting?",
          "Communication \u2014 Regular updates, AMAs, transparent roadmap",
          "LinkedIn/GitHub profiles \u2014 Verify employment history and code contributions",
        ],
      },
      {
        heading: "Red Flags",
        items: [
          "Fully anonymous team with large token allocation",
          "Team tokens already fully unlocked",
          "No development activity visible on GitHub",
          "Over-promising with no shipping history",
          "Key team members leaving without explanation",
          "Team selling tokens while marketing to retail buyers",
        ],
      },
      {
        heading: "Green Flags",
        items: [
          "Long vesting schedules for team tokens (3-4 years)",
          "Founders with prior successful exits or projects",
          "Active open-source development",
          "Regular, honest communication including about setbacks",
          "Team members widely respected in their field",
        ],
      },
    ],
  },
  {
    id: "partnerships",
    icon: Handshake,
    color: "#f97316",
    title: "Partnerships & Integrations",
    subtitle: "Network effects and ecosystem strength",
    content: [
      {
        heading: "Why Partnerships Matter",
        text: "Real partnerships create network effects \u2014 they bring users, capital, and credibility. But in crypto, 'partnership announcements' are notoriously overblown. Learn to distinguish real integrations from marketing fluff.",
      },
      {
        heading: "Types of Partnerships",
        items: [
          "Technical integrations \u2014 Other protocols building on or connecting to the project (strongest signal)",
          "Enterprise adoption \u2014 Real companies using the technology in production",
          "Exchange listings \u2014 Major exchange listings (Coinbase, Binance) signal credibility",
          "Institutional backing \u2014 Investment from VCs like a16z, Paradigm, Sequoia",
          "Cross-chain bridges \u2014 Interoperability with other ecosystems",
          "Audit partnerships \u2014 Security firms vetting the code",
        ],
      },
      {
        heading: "How to Verify",
        items: [
          "Check the partner's website/docs \u2014 do THEY mention the integration?",
          "Look for on-chain evidence (contract deployments, transaction volume)",
          "Is there a working product, or just an announcement?",
          "Check if 'partnership' is actually just a paid promotion",
          "Look at ecosystem grants \u2014 which projects are building on the platform?",
        ],
      },
      {
        heading: "Evaluating Ecosystem Strength",
        text: "The number of active developers, deployed dApps, and Total Value Locked (TVL) are the strongest indicators of ecosystem health. A blockchain with 100 active projects is fundamentally stronger than one with 5, regardless of token price. Tools: DeFiLlama (TVL), Electric Capital (dev reports), DappRadar (active dApps).",
      },
    ],
  },
  {
    id: "liquidity-pairs",
    icon: Link,
    color: "#22d3ee",
    title: "Liquidity Pairs & Bonded Pricing",
    subtitle: "How paired tokens influence each other's price through bonded liquidity",
    content: [
      {
        heading: "What Are Liquidity Pairs?",
        text: "A liquidity pair is two tokens locked together in a smart contract pool that enables decentralized trading. When you trade on a DEX like Uniswap, PulseX, or Raydium, you're swapping against these pools \u2014 not against another person. The pair's ratio determines the price. For example, in an ETH/USDC pool, the ratio of ETH to USDC in the pool IS the price of ETH. This is fundamentally different from centralized exchanges where an order book matches buyers and sellers.",
      },
      {
        heading: "How Bonded Pricing Works",
        text: "Liquidity pairs use an Automated Market Maker (AMM) formula, typically x \u00d7 y = k (constant product). This means if you buy Token A from an A/B pool, you're depositing Token B and removing Token A. This shifts the ratio: Token A becomes scarcer in the pool (price goes up) while Token B becomes more abundant (its price in that pool goes down). The two tokens are mathematically bonded \u2014 one cannot move without affecting the other.",
      },
      {
        heading: "Price Appreciation Through Bonding",
        text: "When Token A is paired with a strong, high-demand token like ETH or BTC, it benefits from the 'base pair effect.' If ETH rises 20% against USD but the A/ETH ratio stays constant, Token A also rises ~20% in USD terms \u2014 automatically. This is why many small-cap tokens pump when BTC or ETH rally: they're bonded through liquidity pairs. Conversely, if ETH crashes, every token paired with ETH gets dragged down even if nothing changed about that token itself.",
      },
      {
        heading: "Price Depreciation Through Bonding",
        text: "The flip side is equally powerful. If Token A is in an A/ETH pool and large holders sell Token A for ETH, the pool ratio shifts dramatically \u2014 more A, less ETH \u2014 crashing A's price. Thin liquidity amplifies this: a $50K sell in a $200K pool can move the price 25%+. This is why low-liquidity pairs experience extreme volatility. The bonded partner (ETH in this case) absorbs the sell pressure, so ETH barely moves, but the small-cap token gets destroyed.",
      },
      {
        heading: "Types of Liquidity Pairs",
        items: [
          "Stable Pairs (USDC/USDT, DAI/USDC) \u2014 Low volatility, tight spreads, minimal impermanent loss. Used for stablecoin swaps.",
          "Blue-chip Pairs (TOKEN/ETH, TOKEN/BTC) \u2014 Most common. Your token inherits the base pair's USD price movement. High correlation.",
          "Native Chain Pairs (TOKEN/SOL, TOKEN/PLS) \u2014 Tied to the native blockchain token. When SOL pumps, all Solana ecosystem tokens in SOL pairs tend to follow.",
          "Exotic Pairs (TOKEN-A/TOKEN-B) \u2014 Two non-stable, non-major tokens paired. Extremely volatile \u2014 both sides move independently, creating unpredictable price action.",
          "Concentrated Liquidity Pairs (Uniswap V3+) \u2014 LPs choose a price range. More capital-efficient but requires active management. Price moves outside the range leave LPs with 100% of the losing token.",
        ],
      },
      {
        heading: "Impermanent Loss Explained",
        text: "When you provide liquidity, you deposit equal USD value of both tokens. As prices diverge, the AMM rebalances your position \u2014 you end up with more of the depreciating token and less of the appreciating one. This is 'impermanent loss.' It's called 'impermanent' because if prices return to the original ratio, the loss disappears. But if one token collapses (common in small-cap/ETH pairs), the loss becomes very permanent. Example: You deposit $500 ETH + $500 TOKEN-X. TOKEN-X drops 80%. You now hold mostly TOKEN-X and very little ETH. Your position is worth far less than if you'd just held ETH.",
      },
      {
        heading: "How Pairs Affect Price Discovery",
        items: [
          "Arbitrage bots keep prices consistent across pools \u2014 if TOKEN is cheaper on Uniswap than SushiSwap, bots buy low and sell high until prices equalize",
          "Deep liquidity = tighter spreads = more accurate price discovery. Shallow liquidity = easy to manipulate",
          "The primary trading pair (highest volume pool) becomes the price oracle for that token",
          "Multi-hop routing \u2014 if no direct A/B pool exists, DEXs route through intermediary tokens (A \u2192 ETH \u2192 B), each hop impacting both intermediate pair prices",
          "Liquidity migration events (like moving from V2 to V3 pools) can temporarily break price discovery",
        ],
      },
      {
        heading: "Real-World Examples",
        items: [
          "HEX/PLS on PulseChain \u2014 HEX and PLS are deeply bonded. When PLS pumps, HEX tends to follow due to the HEX/PLS pool rebalancing. When HEX stakers end stakes and sell, PLS in the pool gets bought, giving PLS a small bid.",
          "BONK/SOL \u2014 BONK is primarily paired with SOL. Solana's 2023-2024 rally from $20 to $200+ carried BONK's USD price up 10x even before BONK-specific hype kicked in.",
          "ETH/BTC \u2014 The 'flippening' ratio. When ETH/BTC rises, it means ETH is outperforming BTC. This pair is the most-watched macro indicator in crypto.",
          "Stablecoin depegs \u2014 When UST depegged in 2022, every token in UST pairs experienced instant, catastrophic price distortion because one side of every pair was collapsing.",
        ],
      },
      {
        heading: "What to Evaluate",
        items: [
          "Pool depth (TVL) \u2014 How much liquidity is in the primary trading pair? Under $100K is dangerous.",
          "Pool concentration \u2014 Is liquidity spread across many providers or dominated by 1-2 whales who can pull it?",
          "Base pair strength \u2014 Is your token paired with ETH, a stablecoin, or another volatile token? This changes your risk profile entirely.",
          "LP rewards vs. impermanent loss \u2014 Are the farming rewards worth the IL risk?",
          "Lock status \u2014 Is the liquidity locked in a time-lock contract? Unlocked LP = rug pull risk.",
          "Volume/TVL ratio \u2014 A pool with $1M TVL doing $5M daily volume is healthy. $1M TVL with $10K volume means dead liquidity.",
          "Fee tier \u2014 Higher fee tiers (1%) signal expected volatility. Lower tiers (0.05%) signal stable pairs.",
        ],
      },
      {
        heading: "Key Insight",
        text: "Every token's price is ultimately a function of its liquidity pairs. A token with deep, locked liquidity in a strong base pair (ETH, USDC) has a fundamentally different risk profile than one with thin, unlocked liquidity in an exotic pair. Before buying any token, always check: What is it paired with? How deep is the pool? Is the liquidity locked? These three questions will save you from more losses than any technical indicator.",
      },
    ],
  },
];

function TopicCard({ topic, isExpanded, onToggle }) {
  const Icon = topic.icon;
  return (
    <div className="border border-zinc-800/80 rounded-sm overflow-hidden" data-testid={`academy-topic-${topic.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-zinc-900/50 transition-colors"
        data-testid={`academy-toggle-${topic.id}`}
      >
        <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${topic.color}12` }}>
          <Icon size={18} weight="bold" style={{ color: topic.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-zinc-100">{topic.title}</h3>
          <p className="text-[10px] text-zinc-500 mt-0.5">{topic.subtitle}</p>
        </div>
        {isExpanded ? <CaretDown size={14} className="text-zinc-500 flex-shrink-0" /> : <CaretRight size={14} className="text-zinc-500 flex-shrink-0" />}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-0 space-y-4 border-t border-zinc-800/50">
          {topic.content.map((section, idx) => (
            <div key={idx} className="pt-3">
              <h4 className="text-xs font-medium text-zinc-200 mb-2 flex items-center gap-1.5">
                <CheckCircle size={12} style={{ color: topic.color }} />
                {section.heading}
              </h4>
              {section.text && (
                <p className="text-[11px] leading-[1.7] text-zinc-400">{section.text}</p>
              )}
              {section.items && (
                <ul className="space-y-1.5 mt-1">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-400 leading-[1.6]">
                      <span className="w-1 h-1 rounded-full mt-[7px] flex-shrink-0" style={{ backgroundColor: topic.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AcademyContent() {
  const [expanded, setExpanded] = useState(new Set(["supply-demand"]));

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  return (
    <div className="p-5 max-w-[1000px]" data-testid="academy-content">
      {/* Quick nav badges */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {TOPICS.map(t => (
          <button
            key={t.id}
            onClick={() => {
              if (!expanded.has(t.id)) toggle(t.id);
              document.getElementById(`academy-topic-${t.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="text-[10px] px-2 py-1 rounded-sm border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
            data-testid={`academy-nav-${t.id}`}
          >
            {t.title}
          </button>
        ))}
      </div>

      {/* Topic Cards */}
      <div className="space-y-2">
        {TOPICS.map(topic => (
          <div key={topic.id} id={`academy-topic-${topic.id}`}>
            <TopicCard
              topic={topic}
              isExpanded={expanded.has(topic.id)}
              onToggle={() => toggle(topic.id)}
            />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-6 p-4 bg-zinc-900/30 border border-zinc-800/50 rounded-sm" data-testid="academy-disclaimer">
        <p className="text-[10px] text-zinc-600 leading-relaxed">
          <ShieldCheck size={12} className="inline mr-1 text-zinc-500" />
          <strong className="text-zinc-500">Disclaimer:</strong> This educational content is for informational purposes only and does not constitute financial advice. Always do your own research (DYOR) before making investment decisions. Cryptocurrency markets are highly volatile and you can lose your entire investment.
        </p>
      </div>
    </div>
  );
}

export default function Academy() {
  const [tab, setTab] = useState("what-is-crypto");

  return (
    <div data-testid="academy-page">
      <div className="sticky top-0 z-20 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 px-5 pt-4 pb-0">
        <div className="flex items-center gap-2.5 mb-3">
          <Book size={22} weight="bold" className="text-amber-400" />
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Crypto Academy</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto scrollbar-hide -mx-5 px-5 pb-2">
            <TabsList className="h-9 bg-zinc-900/60 border border-amber-500/30 p-0.5 gap-0.5 inline-flex w-max" data-testid="academy-tabs">
              <TabsTrigger
                value="what-is-crypto"
                data-testid="academy-tab-what-is-crypto"
                className="text-[11px] px-4 py-1.5 h-8 gap-1.5 font-medium whitespace-nowrap transition-colors data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300"
              >
                <QuestionIcon size={13} weight="bold" />
                What is Crypto?
              </TabsTrigger>
              <TabsTrigger
                value="academy"
                data-testid="academy-tab-learn"
                className="text-[11px] px-4 py-1.5 h-8 gap-1.5 font-medium whitespace-nowrap transition-colors data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300"
              >
                <Book size={13} weight="bold" />
                Crypto Academy
              </TabsTrigger>
              <TabsTrigger
                value="why-defi"
                data-testid="academy-tab-why"
                className="text-[11px] px-4 py-1.5 h-8 gap-1.5 font-medium whitespace-nowrap transition-colors data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300"
              >
                <Lightning size={13} weight="bold" />
                Why Crypto / DeFi
              </TabsTrigger>
              <TabsTrigger
                value="blockchain"
                data-testid="academy-tab-blockchain"
                className="text-[11px] px-4 py-1.5 h-8 gap-1.5 font-medium whitespace-nowrap transition-colors data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300"
              >
                <Cube size={13} weight="bold" />
                Blockchain
              </TabsTrigger>
            </TabsList>
          </div>
        </Tabs>
      </div>

      {tab === "what-is-crypto" && <WhatIsCrypto />}
      {tab === "academy" && <AcademyContent />}
      {tab === "why-defi" && <WhyCryptoDefi />}
      {tab === "blockchain" && <BlockchainEducation />}
    </div>
  );
}
